<template>
  <div class="sidebar-container">
    <div class="sidebar-header">
      <div class="header-row">
        <h2>酒店库存</h2>
        <span class="total-badge">匹配 {{ filteredHotels.length }} 家</span>
      </div>
      <el-progress v-if="isProcessing" :percentage="processProgress" :stroke-width="2" status="success" class="geocode-progress" />
    </div>

    <div class="search-panel">
      <!-- 展会卡片 -->
      <div class="exhibition-filter-card">
        <div class="card-title">当前展会</div>
        <div class="selected-exhibition" v-if="query.selectedExhibition" @click="exhibitionDialogVisible = true">
          <span class="name">{{ query.selectedExhibition.ExhibitionNameCn }}</span>
          <i class="el-icon-edit"></i>
        </div>
        <el-button v-else type="primary" size="mini" icon="el-icon-search" plain style="width:100%" @click="exhibitionDialogVisible = true">选择目标展会</el-button>
      </div>

      <el-form size="mini" label-position="top" @submit.native.prevent class="main-form">
        <el-form-item label="入住日期" required class="compact-form-item">
          <el-date-picker v-model="apiQuery.dateRange" type="daterange" range-separator="-" value-format="yyyy-MM-dd" style="width: 100%" :picker-options="pickerOptions" :clearable="false" />
        </el-form-item>

        <el-button type="primary" size="small" style="width:100%; margin-bottom: 10px;" @click="handleApiSearch" :loading="isApiLoading">查询酒店</el-button>

        <div class="tag-filters mb-5">
            <el-checkbox-group v-model="localQuery.features" size="mini">
              <el-checkbox-button label="hasWallet">💰 有定金</el-checkbox-button>
              <el-checkbox-button label="isBus">🚌 有班车</el-checkbox-button>
              <el-checkbox-button label="mustBreakfast">🍳 含早</el-checkbox-button>
              <el-checkbox-button label="mustReception">💁 接待外宾</el-checkbox-button>
            </el-checkbox-group>
        </div>

        <el-collapse v-model="activeCollapse" accordion class="custom-collapse">
          <el-collapse-item title="更多筛选 (价格/位置/星级...)" name="1">
            <el-input v-model="apiQuery.keyword" placeholder="酒店名称 / 地址" prefix-icon="el-icon-search" clearable class="mb-10" />
            
            <el-form-item label="预算范围 (¥)" class="compact-form-item">
              <div class="price-filter-container">
                <div class="input-row">
                  <el-input-number v-model="apiQuery.priceRange[0]" :step="100" :min="0" controls-position="right" size="mini" class="price-input" />
                  <span class="separator">-</span>
                  <el-input-number v-model="apiQuery.priceRange[1]" :step="100" :min="0" controls-position="right" size="mini" class="price-input" />
                </div>
                <div class="preset-row">
                  <span v-for="p in pricePresets" :key="p.label" class="price-chip" :class="{active: isPriceActive(p.val)}" @click="applyPricePreset(p.val)">{{ p.label }}</span>
                </div>
              </div>
            </el-form-item>

            <el-form-item label="距离展馆" class="compact-form-item">
              <el-radio-group v-model="localQuery.distance" size="mini">
                <el-radio-button :label="3">3km</el-radio-button>
                <el-radio-button :label="5">5km</el-radio-button>
                <el-radio-button :label="10">10km</el-radio-button>
                <el-radio-button :label="999">不限</el-radio-button>
              </el-radio-group>
            </el-form-item>

            <el-form-item label="星级" class="compact-form-item">
              <el-checkbox-group v-model="localQuery.stars">
                <el-checkbox :label="5">五星/豪华</el-checkbox>
                <el-checkbox :label="4">四星/高档</el-checkbox>
                <el-checkbox :label="3">三星/舒适</el-checkbox>
              </el-checkbox-group>
            </el-form-item>
            
            <el-form-item label="合作类型" class="compact-form-item">
              <div class="stock-type-group">
                <div v-for="opt in stockTypeOptions" :key="opt.val" class="type-chip" :class="{active: localQuery.stockTypes.includes(opt.val)}" @click="toggleStockType(opt.val)">{{ opt.label }}</div>
              </div>
            </el-form-item>
          </el-collapse-item>
        </el-collapse>
      </el-form>

      <div class="active-tags" v-if="activeFilterTags.length > 0">
        <div class="tags-header"><span>已选条件:</span><el-link type="primary" :underline="false" @click="clearLocalFilters">清空</el-link></div>
        <div class="tags-body">
          <el-tag v-for="tag in activeFilterTags" :key="tag.key" closable size="mini" @close="removeFilter(tag)">{{ tag.label }}</el-tag>
        </div>
      </div>
    </div>
    
    <div class="hotel-list" v-loading="isProcessing">
      <div v-if="isProcessing && localHotelList.length > 0" class="loading-overlay">数据处理中...</div>
      <template v-else>
        <hotel-card 
          v-for="hotel in filteredHotels" 
          :key="hotel.HotelId"
          :hotel="hotel"
          :is-active="currentHotelId === hotel.HotelId"
          @click="handleCardClick(hotel)"
        />
      </template>
    </div>

    <!-- 弹窗 -->
    <el-dialog title="选择展会" :visible.sync="exhibitionDialogVisible" width="650px" append-to-body custom-class="exhibition-dialog">
        <div class="exh-filter-row">
          <el-select v-model="exhFilter.city" placeholder="城市" size="small" style="width:90px" @change="handleCityChange">
            <el-option v-for="c in cityOptions" :key="c" :label="c" :value="c" />
          </el-select>
          <el-select v-model="exhFilter.hall" placeholder="展馆" size="small" style="width:160px" clearable @change="handleExhFilterChange">
            <el-option v-for="h in hallOptions" :key="h.Id" :label="h.Name" :value="h.Id" />
          </el-select>
          <el-select v-model="exhFilter.year" placeholder="年份" size="small" style="width:100px" @change="handleExhFilterChange">
            <el-option v-for="y in [2024, 2025, 2026, 2027, 2028]" :key="y" :label="y+'年'" :value="y" />
          </el-select>
          <el-select v-model="exhFilter.month" placeholder="月份" size="small" style="width:90px" clearable @change="handleExhFilterChange">
            <el-option v-for="m in 12" :key="m" :label="m+'月'" :value="m" />
          </el-select>
          <el-input v-model="exhFilter.keyword" placeholder="搜展会名" size="small" prefix-icon="el-icon-search" style="width:150px" clearable @input="handleExhFilterChange" />
        </div>

        <!-- 列表容器 -->
        <div class="exh-list-container" v-loading="loadingExhibitions">
          <div 
            v-for="exh in displayExhibitions" 
            :key="exh.Id" 
            class="exh-item" 
            :class="{'active': query.selectedExhibition && query.selectedExhibition.Id === exh.Id}" 
            @click="selectExhibition(exh)"
          >
            <!-- 左侧日期 -->
            <div class="exh-date">
                <div class="day">{{ exh.BeginDate.substring(8,10) }}</div>
                <div class="month">{{ exh.BeginDate.substring(5,7) }}月</div>
            </div>

            <!-- 中间信息 (升级部分) -->
            <div class="exh-info">
              <div class="exh-name">{{ exh.ExhibitionNameCn }}</div>
              <!-- 新增：展馆显示行 -->
              <div class="exh-hall-row">
                <i class="el-icon-office-building"></i> 
                <span>{{ exh.HallName || '未录入展馆' }}</span>
              </div>
            </div>

            <!-- 右侧选中标识 -->
            <div class="exh-check">
                <i class="el-icon-check" v-if="query.selectedExhibition && query.selectedExhibition.Id === exh.Id"></i>
            </div>
          </div>
          
          <!-- 无数据占位 -->
          <div v-if="displayExhibitions.length === 0 && !loadingExhibitions" class="empty-text" style="text-align:center; padding:40px; color:#999;">
              暂无匹配展会数据
          </div>
        </div>
    </el-dialog>
  </div>
</template>

<script>
import { searchHotels } from '@/api/hotel';
import { getExhibitionList } from '@/api/exhibition';
import { fixHotelCoordinates } from '@/utils/amapGeocoder'; 
import hallsData from '../data/halls.json';

import { hotelLogic } from '@/utils/hotel-logic';
import HotelCard from '@/components/HotelCard.vue';

export default {
  name: 'HotelListSidebar',
  mixins: [hotelLogic],
  components: { HotelCard },
  props: {
    rawHotelList: Array,
    currentHotelId: [Number, String]
  },
  data() {
    return {
      isApiLoading: false, isProcessing: false, processProgress: 0,
      activeCollapse: '', exhibitionDialogVisible: false, loadingExhibitions: false,
      localHotelList: [], rawExhibitions: [],
      apiQuery: { dateRange: [], priceRange: [0, 3000], keyword: '' },
      localQuery: { distance: 999, stars: [], features: [], stockTypes: []},
      query: { selectedExhibition: null },
      exhFilter: { city: '上海', year: new Date().getFullYear(), month: null, hall: null, keyword: '' },
      cityOptions: Object.keys(hallsData),
      stockTypeOptions: [
        { val: 1, label: '主推' }, { val: 4, label: '展会独家' }, { val: 5, label: '全年独家' },
        { val: 2, label: '全款' }, { val: 3, label: '口头' }
      ],
      pricePresets: [
        { label: '500以下', val: [100, 500] }, { label: '500-800', val: [500, 800] },
        { label: '800-1200', val: [800, 1200] }, { label: '1200以上', val: [1200, 99999] }
      ],
      pickerOptions: { disabledDate(time) { return time.getTime() < Date.now() - 8.64e7; } },
      timer: null
    };
  },
  computed: {
    hallOptions() { return hallsData[this.exhFilter.city] || []; },
    displayExhibitions() {
        if (!this.exhFilter.keyword) return this.rawExhibitions;
        return this.rawExhibitions.filter(e => e.ExhibitionNameCn?.toLowerCase().includes(this.exhFilter.keyword.toLowerCase()));
    }
  },
  methods: {
    handleCityChange() {
      this.exhFilter.hall = null;
      this.handleExhFilterChange();
      this.$emit('city-change', this.exhFilter.city);
    },
    handleExhFilterChange() {
      const { year, month, hall, keyword } = this.exhFilter;
      if ((year && month && hall) || (keyword && keyword.trim())) {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.fetchExhibitions(), 500);
      }
    },
    async fetchExhibitions() {
      this.loadingExhibitions = true;
      try {
        const res = await getExhibitionList({ Year: this.exhFilter.year, Month: this.exhFilter.month, Hall: this.exhFilter.hall, Keyword: this.exhFilter.keyword });
        this.rawExhibitions = res.data?.Datas || [];
      } finally { this.loadingExhibitions = false; }
    },
    async handleApiSearch() {
      if (!this.apiQuery.dateRange?.length) return this.$message.warning('请选择日期');
      this.isApiLoading = true;
      try {
        const data = await searchHotels({ ...this.apiQuery, ExhibitionId: this.query.selectedExhibition?.Id || 0 });
        if (data) this.processData(data);
      } finally { this.isApiLoading = false; }
    },
    async processData(data) {
      this.isProcessing = true;
      this.localHotelList = JSON.parse(JSON.stringify(data));
      await fixHotelCoordinates(this.localHotelList, p => this.processProgress = p);
      this.isProcessing = false;
      this.$emit('hotels-filtered', this.filteredHotels);
    },
    selectExhibition(exh) {
      this.query.selectedExhibition = exh;
      this.exhibitionDialogVisible = false;
      
      if (exh.BeginDate && exh.EndDate) {
        // 1. 获取展会原始开始日期字符串 (YYYY-MM-DD)
        const originalBeginStr = exh.BeginDate.substring(0, 10);
        
        // 2. 转换为 Date 对象并减去一天
        const startDate = new Date(originalBeginStr);
        startDate.setDate(startDate.getDate() - 1);
        
        // 3. 格式化回 YYYY-MM-DD 字符串
        const year = startDate.getFullYear();
        const month = String(startDate.getMonth() + 1).padStart(2, '0');
        const day = String(startDate.getDate()).padStart(2, '0');
        const finalBeginStr = `${year}-${month}-${day}`;
        
        // 4. 赋值给日期范围（开始日期为前一天，结束日期保持不变）
        this.apiQuery.dateRange = [finalBeginStr, exh.EndDate.substring(0, 10)];
      }
      
      this.$emit('exhibition-change', exh); 
      this.handleApiSearch();
    },
    handleCardClick(hotel) { this.$emit('hotel-click', hotel); },
    toggleStockType(val) {
      const idx = this.localQuery.stockTypes.indexOf(val);
      idx > -1 ? this.localQuery.stockTypes.splice(idx, 1) : this.localQuery.stockTypes.push(val);
    },
    applyPricePreset(range) {
      this.apiQuery.priceRange = (this.apiQuery.priceRange[0] === range[0]) ? [0, 3000] : [...range];
    },
    isPriceActive(r) {
      // 必须两个数字完全一致才算激活
      return this.apiQuery.priceRange[0] === r[0] && 
            this.apiQuery.priceRange[1] === r[1];
    },
    removeFilter(tag) {
      const q = this.localQuery;
      if (tag.key === 'distance') q.distance = 999;
      else if (tag.type === 'feature') q.features.splice(q.features.indexOf(tag.key), 1);
      else if (tag.type === 'star') q.stars.splice(q.stars.indexOf(tag.val), 1);
      else if (tag.type === 'stock') q.stockTypes.splice(q.stockTypes.indexOf(tag.val), 1);
    },
    clearLocalFilters() {
      Object.assign(this.localQuery, { features: [], distance: 999, stars: [], stockTypes: []});
    }
  },
  watch: {
    filteredHotels(newVal) { if (!this.isProcessing) this.$emit('hotels-filtered', newVal); },
    rawHotelList: { handler(newVal) { if(newVal?.length) this.processData(newVal); }, immediate: true },
    'apiQuery.dateRange'(newVal) { this.$emit('date-range-change', newVal); }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/dashboard/sidebar.scss";

.exh-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.2s;

  &:hover { background: #f9f9f9; }
  &.active { background: #f0f7ff; }

  .exh-info {
    flex: 1;
    margin-left: 15px;
    display: flex;
    flex-direction: column; // 确保纵向排列

    .exh-name {
      font-size: 14px;
      font-weight: bold;
      color: #333;
      margin-bottom: 4px; // 撑开间距
    }

    .exh-hall-row {
      font-size: 12px;
      color: #999;
      display: flex;
      align-items: center;

      i {
        margin-right: 4px;
        color: #409EFF; // 给图标一点颜色，更易识别
      }
    }
  }
  
  .exh-date {
      text-align: center;
      width: 45px;
      .day { font-size: 18px; font-weight: bold; color: #409EFF; }
      .month { font-size: 12px; color: #999; }
  }
}
</style>